import { computed, nextTick, ref } from "vue"
import { EMPTY_GUID } from "../utils/constants"

// Shared definitions of "filled in", so every form means the same thing.
export const hasText = (value, minLength = 1) => typeof value === "string" && value.trim().length >= minLength
export const isSetGuid = (id) => typeof id === "string" && id.length === 36 && id !== EMPTY_GUID
// accepts "12,5" as well as "12.5"
export const toNumber = (value) =>
  value === null || value === undefined || value === "" ? NaN : Number(String(value).replace(",", "."))
export const isNumber = (value) => !isNaN(toNumber(value))
// min inclusive, max exclusive
export const isInRange = (value, min, max) => isNumber(value) && toNumber(value) >= min && toNumber(value) < max

/**
 * Form validation with named fields. Errors stay hidden until the user tries to submit.
 *
 * rules: { key: [check, label] }, or a function returning that (for rules that depend on rows etc.).
 *   check: () => boolean, true when the field is fine; label: what the user sees in the warning.
 *
 * Returns
 *   isValid       true when every rule passes
 *   attempt()     call first in a submit handler: shows the errors, scrolls to the first one, returns isValid
 *   showWarning   true after a failed attempt (or a server rejection), for <RequiredWarning v-show>
 *   missing       labels of failing rules (and fields the server rejected), for <RequiredWarning :missing>
 *   invalid(key)  true when that field should be marked red (is-invalid); only after an attempt
 *   fails(key)    true when that rule fails, regardless of attempts (for logic, not for display)
 *   fromServer(e) marks fields from a server "Body validation failed." response (invalidProperties[].memberNames);
 *                 call it with the caught error after a failed save, and with null before saving again
 *   reset()       hides everything again, e.g. after loading another entity into the same form
 */
export function useValidation(rules) {
  const attempted = ref(false)
  const serverErrors = ref({}) // lower-cased member name -> message

  const failingKeys = computed(() => {
    const current = typeof rules === "function" ? rules() : rules
    return Object.entries(current)
      .filter(([, [check]]) => !check())
      .map(([key, [, label]]) => ({ key, label }))
  })

  const isValid = computed(() => failingKeys.value.length === 0)

  const hasServerErrors = computed(() => Object.keys(serverErrors.value).length > 0)

  const showWarning = computed(() => (attempted.value && !isValid.value) || hasServerErrors.value)

  const missing = computed(() => [
    ...new Set([...failingKeys.value.map((f) => f.label), ...Object.values(serverErrors.value)]),
  ])

  const fails = (key) => failingKeys.value.some((f) => f.key === key)

  const invalid = (key) => (attempted.value && fails(key)) || serverErrors.value[key.toLowerCase()] !== undefined

  const attempt = () => {
    attempted.value = true
    if (!isValid.value) {
      nextTick(() => document.querySelector(".is-invalid")?.scrollIntoView({ block: "center", behavior: "smooth" }))
    }
    return isValid.value
  }

  const fromServer = (error) => {
    const result = {}
    for (const p of error?.invalidProperties ?? []) {
      for (const member of p.memberNames ?? []) {
        result[member.toLowerCase()] = p.errorMessage || member
      }
    }
    serverErrors.value = result
  }

  const reset = () => {
    attempted.value = false
    serverErrors.value = {}
  }

  return { isValid, attempt, showWarning, missing, invalid, fails, fromServer, reset }
}
